import { useState, useRef, useEffect } from "react";
import "./DayPicker.less";
import { Icon } from "semantic-ui-react";
import "react-dates/initialize";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import CalendarIcon from "src/icons/CalendarIcon";
import { useMediaQuery } from "@chakra-ui/react";
import { useTranslate } from "@tolgee/react";
import { useSelector } from "react-redux";
import { RootState } from "src/redux";

/**
 * Extended version of the AirBnB daypicker with our UI customization added
 *  */

interface DayPickerI {
  ariaLabel?: string;
  date?: moment.Moment;
  disabled?: boolean;
  id: string;
  isOutsideRange?: (date: moment.Moment) => boolean;
  onDateChange: (date: moment.Moment) => void;
  placeholder?: string;
  showClearDate?: boolean;
}

const DayPicker = ({
  ariaLabel,
  date,
  disabled = false,
  id,
  isOutsideRange,
  onDateChange,
  placeholder,
  showClearDate,
}: DayPickerI) => {
  const { t } = useTranslate();
  const language = useSelector((state: RootState) => state.user.language);
  const localizedDate = date?.clone().locale(language);
  if (!placeholder)
    placeholder = t({
      key: "components.datePicker.placeholder",
      defaultValue: "Select Date",
    });
  const [focused, setFocused] = useState(false);
  const [isLessThan600] = useMediaQuery("(max-width: 600px)");
  const focusedRef = useRef(false);
  const containerRef = useRef(null);
  const checkRange = isOutsideRange ? { isOutsideRange } : {};

  useEffect(() => {
    function listener({ key }) {
      if (focusedRef.current && key === "Tab") {
        const el = document.getElementById(id) as HTMLElement;
        if (el) {
          el.focus();
        }
        setFocus({ focused: false });
      }
    }
    if (focused) {
      window.addEventListener("keydown", listener, true);
      focusOnDate();
    } else {
      window.removeEventListener("keydown", listener);
    }
    return () => window.removeEventListener("keydown", listener);
  }, [focused]);

  function setFocus({ focused }) {
    setFocused(focused);
    focusedRef.current = focused;
  }

  function setFocusOnButton() {
    setTimeout(() => {
      const target = containerRef.current.getElementsByClassName(
        "SingleDatePickerInput_calendarIcon",
      )[0] as HTMLButtonElement;

      target?.focus();
    }, 250);
  }

  function focusOnDate() {
    setTimeout(() => {
      const days = document.getElementsByClassName("CalendarDay") || [];
      let el;
      for (let i = 0; i < days.length; i++) {
        if (days[i].tabIndex === 0) {
          el = days[i];
          break;
        }
      }
      if (el) {
        el.focus();
      }
    }, 250);
  }

  const renderMonthElement = ({ month, onYearSelect }) => {
    const localizedMonth = month.clone().locale(language);

    return (
      <div className="monthContainer">
        <Icon
          onClick={() => onYearSelect(month, month.year() - 1)}
          name="angle double left"
          className="caret prevYear"
        />
        <strong>{localizedMonth.format("MMMM YYYY")}</strong>
        <Icon
          onClick={() => onYearSelect(month, month.year() + 1)}
          name="angle double right"
          className="caret nextYear"
        />
      </div>
    );
  };

  return (
    <div className="day-picker" ref={containerRef}>
      <SingleDatePicker
        key={language}
        {...checkRange}
        ariaLabel={ariaLabel}
        hideKeyboardShortcutsPanel={true}
        displayFormat="ll"
        disabled={disabled}
        withPortal
        navNext={<Icon name="caret right" className="caret" />}
        navPrev={<Icon name="caret left" className="caret" />}
        renderMonthElement={renderMonthElement}
        numberOfMonths={isLessThan600 ? 1 : 2}
        placeholder={placeholder}
        customInputIcon={
          <CalendarIcon
            style={{ color: "#ABB1BB" }}
            className="SingleDatePickerInput_calendarIcon_svg"
            aria-label={t({
              key: "components.datePicker.openDatePicker",
              defaultValue: "Open Date Picker",
            })}
          />
        }
        showDefaultInputIcon={true}
        date={localizedDate}
        onDateChange={(date) => {
          onDateChange(date);
          setFocusOnButton();
        }}
        focused={focused}
        onFocusChange={setFocus}
        id={id}
        showClearDate={showClearDate}
      />
    </div>
  );
};

export default DayPicker;
